/**
 * Dashboard tile — your own training progress, with a way straight back in.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { GraduationCap, Printer, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const BAR = {
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  indigo: "bg-indigo-500",
};

export const TrainingProgressCard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/training/tracks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setTracks(r.data.tracks || []))
      .catch(() => setTracks([]));
  }, [token]);

  if (!tracks.length) return null;
  const done = tracks.filter((t) => t.progress?.completed_at).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="dashboard-training-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-blue-600">
            Training &amp; tutorials
          </p>
          <h3 className="text-lg font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-blue-600" />
            {done} of {tracks.length} tracks complete
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Narrated walkthroughs of the Sourcing Desk, Bid Desk and Project Management portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => openAuthedDoc("/training/quick-cards", { token })}
            data-testid="dashboard-training-cards">
            <Printer className="w-3.5 h-3.5 mr-1" /> Reference cards
          </Button>
          <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate("/admin/training")}
            data-testid="dashboard-training-open">
            Training Center <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {tracks.map((t) => {
          const watched = (t.progress?.watched_steps || []).length;
          const pct = Math.round((watched / Math.max(t.step_count, 1)) * 100);
          return (
            <button key={t.id} onClick={() => navigate(`/admin/training?track=${t.id}`)}
              className="text-left rounded-lg border border-slate-200 hover:border-blue-400 p-3 transition-colors"
              data-testid={`dashboard-training-${t.id}`}>
              <p className="text-[13px] font-bold text-slate-900 leading-snug">
                {t.name.split(" — ")[0]}
              </p>
              <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden">
                <div className={`h-1.5 ${BAR[t.accent] || BAR.sky}`} style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                {t.progress?.completed_at
                  ? `Completed ${new Date(t.progress.completed_at).toLocaleDateString()}`
                  : watched
                    ? `Resume — ${watched} of ${t.step_count} watched`
                    : `Start — ${t.step_count} steps`}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TrainingProgressCard;
